import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Spin } from 'antd';
import OWRadioGroup from '../ow_radio_group/OWRadioGroup';
import {
	nullSafeGet,
	nullSafeGetOrElse,
	renderCurrencyByCurrencyId,
} from '../../utils/DataAccessUtils';

interface PartEquipmentPricePickerProps {
	entityId: any;
	entityName: 'partId' | 'equipmentTypeId';
	vendorId: any;
	fetchMethod: any;
	fallbackCurrencyId: any;
	value: any;
	onChange: (unitPrice: any) => void;
	onCatalogLoad?: (catalog: any, catalogCurrencyId: any) => void;
	disabled?: boolean;
	defaultToListPrice?: boolean;
	entityCost?: any;
}

const PartEquipmentPricePicker: FC<PartEquipmentPricePickerProps> = ({
	entityId,
	entityName,
	vendorId,
	fetchMethod,
	fallbackCurrencyId,
	value,
	onChange,
	onCatalogLoad,
	disabled,
	defaultToListPrice,
	entityCost,
}) => {
	const [catalog, setCatalog] = useState(null);
	// Treat a pending vendor fetch as "loading" from the very first render so the
	// default-selection logic below never reads an empty catalog and wrongly
	// falls back to cost before the real list/last price has had a chance to
	// arrive. Without a vendor there is no fetch, so we start un-loaded.
	const [catalogLoading, setCatalogLoading] = useState(() => !!(entityId && vendorId));

	const refreshCatalog = useCallback(
		(id, partEquipmentVendorId) => {
			setCatalogLoading(true);
			fetchMethod(
				{
					[entityName]: id,
					partEquipmentVendorId,
				},
				'add_in_pr_index'
			)
				.then((res) => {
					const nextCatalog = res && res.length > 0 ? res[0] : null;
					setCatalog(nextCatalog);
				})
				.finally(() => {
					setCatalogLoading(false);
				});
		},
		[entityName, fetchMethod]
	);

	useEffect(() => {
		if (entityId && vendorId) {
			refreshCatalog(entityId, vendorId);
		} else {
			// No vendor to fetch a catalog from — there is no pending request, so
			// settle the loading state and let any cost fallback render.
			setCatalog(null);
			setCatalogLoading(false);
		}
	}, [entityId, refreshCatalog, vendorId]);

	const catalogCurrencyId = useMemo(
		() => nullSafeGetOrElse('partEquipmentVendor.currencyId', catalog, fallbackCurrencyId),
		[catalog, fallbackCurrencyId]
	);

	useEffect(() => {
		if (!catalogLoading && onCatalogLoad) {
			onCatalogLoad(catalog, catalogCurrencyId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [catalog, catalogCurrencyId, catalogLoading]);

	const listPrice = nullSafeGet('listPrice', catalog);
	const lastPrice = nullSafeGet('lastPrice', catalog);

	// Fall back to the entity's own cost ONLY when neither list nor last price is
	// available. Gating on the same truthiness the List/Last options use
	// guarantees the Cost option and a List/Last option can never appear together.
	const costFallback = useMemo(() => {
		if (listPrice || lastPrice) return null;
		if (entityCost === null || entityCost === undefined || entityCost === '') return null;
		const numericCost = parseFloat(entityCost);
		return isNaN(numericCost) ? null : numericCost;
	}, [listPrice, lastPrice, entityCost]);

	// When defaultToListPrice is enabled, auto-select a default the moment the
	// catalog has settled — but only if the consumer hasn't already set a value.
	// Priority: List Price, else Last Price, else the entity cost fallback.
	// Intentionally depends on `catalog` only so that clearing or manually
	// changing `value` later does NOT re-fire this effect. The catalogLoading
	// guard ensures cost is only chosen after list/last are genuinely absent.
	useEffect(() => {
		if (!defaultToListPrice) return;
		if (catalogLoading) return;
		const valueIsEmpty = value === undefined || value === null || value === '';
		if (!valueIsEmpty) return;
		if (listPrice) {
			onChange(parseFloat(listPrice));
		} else if (lastPrice) {
			onChange(parseFloat(lastPrice));
		} else if (costFallback !== null) {
			onChange(costFallback);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [catalog]);

	const selectedValue = useMemo(() => {
		if (value === undefined || value === null || value === '') return null;
		const numericValue = parseFloat(value);
		if (isNaN(numericValue)) return null;
		if (listPrice && parseFloat(listPrice) === numericValue) return numericValue;
		if (lastPrice && parseFloat(lastPrice) === numericValue) return numericValue;
		if (costFallback !== null && costFallback === numericValue) return numericValue;
		return null;
	}, [costFallback, lastPrice, listPrice, value]);

	const onPriceSelect = useCallback(
		(unitPrice) => {
			onChange(unitPrice);
		},
		[onChange]
	);

	if (catalogLoading) {
		return (
			<div className="ml-2">
				<Spin size="small" />
			</div>
		);
	}

	if (!listPrice && !lastPrice && costFallback === null) {
		return null;
	}

	return (
		<div className="ml-2">
			<OWRadioGroup
				value={selectedValue}
				onChange={onPriceSelect}
				disabled={disabled}
				valueAccessor={(item) => item.value}
				renderRecord={(item) => item.label}
				items={[
					...(listPrice
						? [
								{
									label: `List Price ${renderCurrencyByCurrencyId(catalogCurrencyId)(listPrice)}`,
									value: parseFloat(listPrice),
								},
						  ]
						: []),
					...(lastPrice
						? [
								{
									label: `Last Price ${renderCurrencyByCurrencyId(catalogCurrencyId)(lastPrice)}`,
									value: parseFloat(lastPrice),
								},
						  ]
						: []),
					...(costFallback !== null
						? [
								{
									label: `Cost ${renderCurrencyByCurrencyId(catalogCurrencyId)(costFallback)}`,
									value: costFallback,
								},
						  ]
						: []),
				]}
			/>
		</div>
	);
};

export default PartEquipmentPricePicker;
